import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type {
  ProjectoCampaign,
  ProjectoCampaignInsert,
  ProjectoPLItem,
  ProjectoPLItemInsert,
  ProjectoInvoice,
  ProjectoInvoiceInsert,
  ProjectoActivityLog,
  ProjectoPLSummary,
  ProjectoItemFigures,
  ProjectoPassthroughCost,
  ProjectoEntryKind,
} from '../types/projecto';

// Columns a user may edit on a project. Anything else the form holds
// (id, work_code, created_by, created_at, updated_at) must never be sent.
const PROJECTO_CAMPAIGN_EDITABLE_FIELDS = [
  'name',
  'client_id',
  'client_name',
  'brand',
  'reference',
  'status',
  'start_date',
  'finish_date',
  'notes',
  'drive_link',
  'budget_enabled',
  'budget_amount',
  'target_margin_pct',
  'default_commission_pct',
  'admin_only',
  'account_id',
] as const;

function sanitizeProjectoCampaignUpdates(
  updates: Partial<ProjectoCampaignInsert>
): Record<string, unknown> {
  const clean: Record<string, unknown> = {};
  for (const key of PROJECTO_CAMPAIGN_EDITABLE_FIELDS) {
    if (key in updates) clean[key] = (updates as Record<string, unknown>)[key];
  }
  return clean;
}

/** Everyone a project can be assigned to. */
export async function listStaffAccounts(): Promise<{ user_id: string; name: string }[]> {
  const { data, error } = await (supabase as unknown as SupabaseClient).rpc('list_staff_accounts');
  if (error) throw error;
  return (data || []) as { user_id: string; name: string }[];
}

// ─── Campaigns ────────────────────────────────────────────────────────────────

export const projectoCampaignService = {
  async getAll(): Promise<ProjectoCampaign[]> {
    const { data, error } = await supabase
      .from('projecto_campaigns')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data ?? [];
  },

  async getById(id: string): Promise<ProjectoCampaign | null> {
    const { data, error } = await supabase
      .from('projecto_campaigns')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async generateNextWorkCode(year: number): Promise<string> {
    const { data, error } = await supabase
      .from('projecto_campaigns')
      .select('work_code')
      .like('work_code', `P${year}/%`)
      .order('created_at', { ascending: false });

    if (error) throw error;

    let maxSequence = 0;
    if (data && data.length > 0) {
      data.forEach((item: { work_code: string | null }) => {
        if (!item.work_code) return;
        const match = item.work_code.match(/\/(\d+)/);
        if (match) {
          const num = parseInt(match[1]);
          if (num > maxSequence) maxSequence = num;
        }
      });
    }

    return `P${year}/${maxSequence + 1}`;
  },

  async claimNextWorkCode(year: number): Promise<string> {
    const { data, error } = await supabase.rpc('generate_next_projecto_work_code', { p_year: year });
    if (error) throw error;
    return data;
  },

  async create(insert: ProjectoCampaignInsert): Promise<ProjectoCampaign> {
    const { data: { user } } = await supabase.auth.getUser();
    const year = new Date().getFullYear();
    const workCode = await projectoCampaignService.claimNextWorkCode(year);
    const { data, error } = await supabase
      .from('projecto_campaigns')
      .insert({ ...insert, work_code: workCode, created_by: user?.id ?? null })
      .select()
      .single();

    if (error) throw error;

    await projectoActivityService.log(data.id, user?.email ?? '', 'created', {});
    return data;
  },

  async update(id: string, updates: Partial<ProjectoCampaignInsert>): Promise<ProjectoCampaign> {
    const { data: { user } } = await supabase.auth.getUser();

    // Callers pass whole form state, which carries read-only columns
    // (id, work_code, created_by, created_at, updated_at) and sometimes keys
    // that are not columns at all - PostgREST rejects the whole request then.
    const clean = sanitizeProjectoCampaignUpdates(updates);
    if (Object.keys(clean).length === 0) {
      const current = await projectoCampaignService.getById(id);
      if (!current) throw new Error('Project not found');
      return current;
    }

    const { data, error } = await supabase
      .from('projecto_campaigns')
      .update(clean)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    await projectoActivityService.log(id, user?.email ?? '', 'updated', { fields: Object.keys(updates) });
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('projecto_campaigns')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ─── P&L Items ────────────────────────────────────────────────────────────────

export const projectoPLService = {
  /** Every line of every project the user can see, for the list's margins. */
  async getAll(): Promise<ProjectoPLItem[]> {
    const { data, error } = await supabase.from('projecto_pl_items').select('*');
    if (error) throw error;
    return data ?? [];
  },

  async getByProjectoId(projectoId: string): Promise<ProjectoPLItem[]> {
    const { data, error } = await supabase
      .from('projecto_pl_items')
      .select('*')
      .eq('projecto_id', projectoId)
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data ?? [];
  },

  async create(insert: ProjectoPLItemInsert): Promise<ProjectoPLItem> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('projecto_pl_items')
      .insert(insert)
      .select()
      .single();

    if (error) throw error;

    await projectoActivityService.log(
      insert.projecto_id,
      user?.email ?? '',
      'item_added',
      { item_name: data.name, is_revenue: data.is_revenue }
    );
    return data;
  },

  async update(id: string, projectoId: string, updates: Partial<ProjectoPLItemInsert>): Promise<ProjectoPLItem> {
    const { data, error } = await supabase
      .from('projecto_pl_items')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string, projectoId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data: item } = await supabase.from('projecto_pl_items').select('name').eq('id', id).maybeSingle();

    const { error } = await supabase
      .from('projecto_pl_items')
      .delete()
      .eq('id', id);

    if (error) throw error;

    await projectoActivityService.log(projectoId, user?.email ?? '', 'item_removed', { item_name: item?.name ?? '' });
  },

  async updateOrder(items: { id: string; display_order: number }[]): Promise<void> {
    const updates = items.map(({ id, display_order }) =>
      supabase.from('projecto_pl_items').update({ display_order }).eq('id', id)
    );
    await Promise.all(updates);
  },
};

// ─── Invoices ─────────────────────────────────────────────────────────────────

export const projectoInvoiceService = {
  async getAll(): Promise<ProjectoInvoice[]> {
    const { data, error } = await supabase.from('projecto_invoices').select('*');
    if (error) throw error;
    return data ?? [];
  },

  async getByProjectoId(projectoId: string): Promise<ProjectoInvoice[]> {
    const { data, error } = await supabase
      .from('projecto_invoices')
      .select('*')
      .eq('projecto_id', projectoId)
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) throw error;

    const now = new Date();
    return (data ?? []).map((inv) => ({
      ...inv,
      is_overdue:
        (inv.status === 'Issued' || inv.status === 'Partially Paid') &&
        inv.due_date != null &&
        new Date(inv.due_date) < now,
    }));
  },

  async create(insert: ProjectoInvoiceInsert): Promise<ProjectoInvoice> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('projecto_invoices')
      .insert(insert)
      .select()
      .single();

    if (error) throw error;

    await projectoActivityService.log(
      insert.projecto_id,
      user?.email ?? '',
      'invoice_added',
      { invoice_type: insert.invoice_type, number: insert.number }
    );
    return data;
  },

  async update(id: string, projectoId: string, updates: Partial<ProjectoInvoiceInsert>): Promise<ProjectoInvoice> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('projecto_invoices')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    await projectoActivityService.log(projectoId, user?.email ?? '', 'invoice_updated', { fields: Object.keys(updates) });
    return data;
  },

  async delete(id: string, projectoId: string): Promise<void> {
    const { error } = await supabase
      .from('projecto_invoices')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ─── Activity Logs ────────────────────────────────────────────────────────────

export const projectoActivityService = {
  async getByProjectoId(projectoId: string): Promise<ProjectoActivityLog[]> {
    const { data, error } = await supabase
      .from('projecto_activity_logs')
      .select('*')
      .eq('projecto_id', projectoId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data ?? [];
  },

  async log(
    projectoId: string,
    userEmail: string,
    action: ProjectoActivityLog['action'],
    details: Record<string, unknown>
  ): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('projecto_activity_logs').insert({
      projecto_id: projectoId,
      user_id: user?.id ?? null,
      user_email: userEmail,
      action,
      details,
    });
  },
};

// ─── P&L Calculator ───────────────────────────────────────────────────────────

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

export function getItemEntryKind(item: ProjectoPLItem): ProjectoEntryKind {
  if (item.entry_kind) return item.entry_kind;
  if (item.is_revenue) return 'revenue';
  if (item.cost_billing_mode === 'reimbursed') return 'passthrough';
  return 'cost';
}

/**
 * What the agency pays on a line. Internal costs used to keep their value in
 * quantity × unit price instead of a list; those are read as a single row.
 */
export function getItemCostRows(item: ProjectoPLItem): ProjectoPassthroughCost[] {
  const rows = Array.isArray(item.passthrough_breakdown) ? item.passthrough_breakdown : [];
  if (rows.length === 0 && getItemEntryKind(item) === 'cost' && Number(item.total_price) > 0) {
    return [{
      id: `legacy-${item.id}`,
      label: item.vendor_name ?? '',
      amount: Number(item.total_price),
      quantity: Number(item.quantity) || 1,
      unit_cost: Number(item.unit_price) || 0,
      paid: item.payment_status === 'Paid',
    }];
  }
  return rows;
}

export function getItemFigures(item: ProjectoPLItem): ProjectoItemFigures {
  const kind = getItemEntryKind(item);
  const rows = getItemCostRows(item);
  const cost = round2(rows.reduce((s, c) => s + (Number(c?.amount) || 0), 0));
  const paidCost = round2(rows.filter((c) => c?.paid).reduce((s, c) => s + (Number(c?.amount) || 0), 0));
  // An internal cost is never charged to the client
  const charge = kind === 'cost' ? 0 : round2(Number(item.total_price) || 0);
  return {
    kind,
    cost,
    charge,
    margin: round2(charge - cost),
    paidCost,
    missingCost: kind === 'passthrough' && cost === 0 && charge > 0,
  };
}

/** Adds a commission (in % of the cost) to a cost. */
export function chargeFromCommission(cost: number, commissionPct: number): number {
  return round2(cost * (1 + commissionPct / 100));
}

/** The commission (in % of the cost) a charge amounts to; 0 without a cost. */
export function commissionPctOf(cost: number, charge: number): number {
  return cost > 0 ? round2(((charge - cost) / cost) * 100) : 0;
}

type InvoiceAmounts = Pick<ProjectoInvoice, 'invoice_type' | 'status' | 'subtotal' | 'total' | 'amount_paid' | 'vat_rate'>;

/**
 * What came in, without VAT. A paid invoice counts in full: `amount_paid` was
 * typed by hand and holds a mix of amounts with and without VAT.
 */
function receivedWithoutVat(invoice: InvoiceAmounts): number {
  if (invoice.status === 'Paid') return Number(invoice.subtotal) || 0;
  return (Number(invoice.amount_paid) || 0) / (1 + (Number(invoice.vat_rate) || 0));
}

export function computeProjectoPLSummary(
  items: ProjectoPLItem[],
  invoices: InvoiceAmounts[],
  project?: Pick<ProjectoCampaign, 'budget_enabled' | 'budget_amount'> | Partial<ProjectoCampaign> | null
): ProjectoPLSummary {
  const figures = items.map(getItemFigures);
  const sum = (kind: ProjectoEntryKind, pick: (f: ProjectoItemFigures) => number) =>
    round2(figures.filter((f) => f.kind === kind).reduce((s, f) => s + pick(f), 0));

  const ownServices = sum('revenue', (f) => f.charge);
  const supplierCharged = sum('passthrough', (f) => f.charge);
  const supplierCosts = sum('passthrough', (f) => f.cost);
  const internalCosts = sum('cost', (f) => f.cost);
  const ownServiceCosts = sum('revenue', (f) => f.cost);

  const fixedBudget = !!project?.budget_enabled;
  const linesCharged = round2(ownServices + supplierCharged);
  const revenue = fixedBudget ? round2(Number(project?.budget_amount) || 0) : linesCharged;
  const costs = round2(supplierCosts + internalCosts + ownServiceCosts);
  const margin = round2(revenue - costs);

  // Lines with no cost yet are left out: their whole value is not a commission
  const withCost = figures.filter((f) => f.kind === 'passthrough' && !f.missingCost);
  const commissionBase = withCost.reduce((s, f) => s + f.cost, 0);
  const commission = round2(withCost.reduce((s, f) => s + f.margin, 0));

  const paidCosts = round2(figures.reduce((s, f) => s + f.paidCost, 0));

  const live = invoices.filter((i) => i.status !== 'Cancelled' && i.status !== 'Draft');
  const receivables = live.filter((i) => i.invoice_type === 'Receivable');
  const invoiced = round2(receivables.reduce((s, i) => s + (Number(i.subtotal) || 0), 0));
  const unpaid = (i: InvoiceAmounts) =>
    i.status === 'Paid' ? 0 : Math.max(0, (Number(i.total) || 0) - (Number(i.amount_paid) || 0));

  return {
    fixedBudget,
    revenue,
    costs,
    margin,
    marginPct: revenue > 0 ? round2((margin / revenue) * 100) : 0,
    linesCharged,
    ownServices,
    supplierCosts,
    supplierCharged,
    commission,
    commissionPct: commissionBase > 0 ? round2((commission / commissionBase) * 100) : 0,
    internalCosts,
    missingCostLines: fixedBudget ? 0 : figures.filter((f) => f.missingCost).length,
    paidCosts,
    pendingCosts: round2(Math.max(0, costs - paidCosts)),
    invoiced,
    toInvoice: round2(revenue - invoiced),
    received: round2(receivables.reduce((s, i) => s + receivedWithoutVat(i), 0)),
    outstandingReceivables: round2(receivables.reduce((s, i) => s + unpaid(i), 0)),
    outstandingPayables: round2(live.filter((i) => i.invoice_type === 'Payable').reduce((s, i) => s + unpaid(i), 0)),
  };
}
